import React, { useState, useEffect } from 'react';
import { getSupabaseClient } from '../../lib/supabase';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Truck, Users, AlertTriangle, RefreshCw, Ban, ChevronDown, ChevronUp, Star, ShieldCheck } from 'lucide-react';

interface Tenant {
  id: string;
  business_name: string;
  business_type: string;
  status: string;
  transport_operator_type?: 'individual_driver' | 'fleet_business' | 'public_transport' | string;
  service_mode?: string;
  created_at: string;
}

interface ServiceProvider {
  id: string;
  tenant_id: string;
  full_name: string;
  is_online: boolean;
  rating?: number;
  vehicle_number?: string;
}

interface ExpiryAlert {
  document_id: string;
  full_name: string;
  provider_type: string;
  document_type: string;
  expiry_date: string;
  is_expired: boolean;
  days_remaining: number;
}

export function TransportFleetControlView() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [expiryAlerts, setExpiryAlerts] = useState<ExpiryAlert[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [blockingId, setBlockingId] = useState<string | null>(null);
  const [expandedFleets, setExpandedFleets] = useState<Record<string, boolean>>({});
  const [confirmAction, setConfirmAction] = useState<{ tenantId: string, tenantName: string, nextStatus: 'suspended' | 'active' } | null>(null);

  const fetchData = async () => {
    setLoading(true);
    const supabase = getSupabaseClient();
    if (!supabase) {
      setLoading(false);
      return;
    }
    try {
      // 1. Fetch Logistics Tenants
      const { data: tenantData, error: tenantErr } = await supabase
        .from('tenants')
        .select('*')
        .eq('business_type', 'logistics');

      if (tenantErr) throw tenantErr;
      const currentTenants = tenantData || [];
      setTenants(currentTenants);
      console.log('TransportFleetControlView - Raw tenants fetched:', currentTenants);

      // 2. Fetch service providers for these tenants
      if (currentTenants.length > 0) {
        const tenantIds = currentTenants.map((t: Tenant) => t.id);
        const { data: providerData, error: providerErr } = await supabase
          .from('service_providers')
          .select('*')
          .in('tenant_id', tenantIds);

        if (providerErr) throw providerErr;
        setProviders(providerData || []);
      } else {
        setProviders([]);
      }

      // 3. Fetch Document Expiry Alerts
      const { data: alertData, error: alertErr } = await supabase
        .from('v_document_expiry_alerts')
        .select('*');

      if (!alertErr && alertData) {
        setExpiryAlerts(alertData);
      }
    } catch (err: any) {
      toast.error(err.message || 'Error loading Transport Control data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const executeSetTenantStatus = async (tenantId: string, status: 'suspended' | 'active') => {
    setBlockingId(tenantId);
    const supabase = getSupabaseClient();
    if (!supabase) {
      setBlockingId(null);
      return;
    }
    try {
      const { error } = await supabase.rpc('admin_set_tenant_status', {
        p_tenant_id: tenantId,
        p_status: status
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success(`Operator successfully ${status === 'suspended' ? 'suspended' : 'activated'}`);
        // Update local state to reflect change immediately
        setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, status: status } : t));
        fetchData(); // Trigger full refresh to sync backend dependencies
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to update operator status');
    } finally {
      setBlockingId(null);
    }
  };

  const toggleExpandFleet = (fleetId: string) => {
    setExpandedFleets(prev => ({
      ...prev,
      [fleetId]: !prev[fleetId]
    }));
  };

  // Grouped datasets
  const individualRiders = tenants.filter(t => 
    t.transport_operator_type === 'individual_driver' || 
    t.transport_operator_type === 'individual' || 
    t.transport_operator_type === 'driver' || 
    t.service_mode === 'individual'
  );
  const fleetBusinesses = tenants.filter(t => 
    t.transport_operator_type === 'fleet_business' || 
    t.transport_operator_type === 'fleet' || 
    t.transport_operator_type === 'fleet_owner' || 
    t.service_mode === 'business'
  );
  const publicTransports = tenants.filter(t => 
    t.transport_operator_type === 'public_transport' || 
    t.transport_operator_type === 'public'
  );
  console.log('TransportFleetControlView - fleetBusinesses count:', fleetBusinesses.length, fleetBusinesses);

  const expiredAlerts = expiryAlerts.filter(a => a.is_expired);
  const soonExpiringAlerts = expiryAlerts.filter(a => !a.is_expired && a.days_remaining <= 30);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* Title & Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Truck className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
            <span>Transport & Fleet Control</span>
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Monitor logistics providers, riders, fleet businesses, and active drivers.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh Control</span>
        </Button>
      </div>

      {/* DOCUMENT EXPIRY ALERT BANNER */}
      {expiryAlerts.length > 0 && (
        <div className="space-y-3">
          {expiredAlerts.length > 0 && (
            <div className="p-4 rounded-xl border border-red-200 bg-red-50/50 dark:border-red-900/60 dark:bg-red-950/20 text-red-800 dark:text-red-400">
              <div className="flex items-center gap-2 font-bold text-sm mb-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span>{expiredAlerts.length} Documents EXPIRED</span>
              </div>
              <div className="max-h-24 overflow-y-auto text-xs space-y-1 divide-y divide-red-100/50 dark:divide-red-900/20 pr-2">
                {expiredAlerts.map((alert, idx) => (
                  <div key={idx} className="pt-1 first:pt-0">
                    <strong>{alert.full_name}</strong> ka <strong>{alert.document_type}</strong> ({alert.provider_type}) expire ho chuka hai (Expired on {alert.expiry_date}).
                  </div>
                ))}
              </div>
            </div>
          )}

          {soonExpiringAlerts.length > 0 && (
            <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/50 dark:border-amber-900/60 dark:bg-amber-950/20 text-amber-800 dark:text-amber-400">
              <div className="flex items-center gap-2 font-bold text-sm mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span>{soonExpiringAlerts.length} Documents Expiring Soon (Within 30 Days)</span>
              </div>
              <div className="max-h-24 overflow-y-auto text-xs space-y-1 divide-y divide-amber-100/50 dark:divide-amber-900/20 pr-2">
                {soonExpiringAlerts.map((alert, idx) => (
                  <div key={idx} className="pt-1 first:pt-0">
                    <strong>{alert.full_name}</strong> ka <strong>{alert.document_type}</strong> {alert.days_remaining} din mein expire ho raha hai.
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center h-64 gap-2">
          <RefreshCw className="h-8 w-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
          <p className="text-sm text-slate-500 dark:text-slate-400">Fetching riders and fleet details...</p>
        </div>
      ) : (
        <div className="space-y-8">

          {/* SECTION A: INDIVIDUAL RIDERS */}
          <div className="space-y-3">
            <h3 className="text-md font-bold text-slate-900 dark:text-slate-150 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              <span>Individual Riders ({individualRiders.length})</span>
            </h3>
            {individualRiders.length === 0 ? (
              <p className="text-xs text-slate-400 dark:text-slate-500 italic p-4 border border-dashed rounded-lg">
                No active individual riders found.
              </p>
            ) : (
              <Card className="dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-400 font-bold border-b border-slate-200 dark:border-slate-800">
                    <tr>
                      <th className="px-6 py-3">Rider / Business Name</th>
                      <th className="px-6 py-3">Driver Name</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3">Vehicle Details</th>
                      <th className="px-6 py-3">Rating</th>
                      <th className="px-6 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-150 dark:divide-slate-800/60">
                    {individualRiders.map((rider) => {
                      // Get service provider for this rider
                      const prov = providers.find(p => p.tenant_id === rider.id);
                      return (
                        <tr key={rider.id} className="hover:bg-slate-50/40 dark:hover:bg-slate-950/20 text-xs">
                          <td className="px-6 py-4">
                            <span className="font-bold text-slate-900 dark:text-white block">{rider.business_name}</span>
                            <span className="text-[10px] text-slate-400">ID: {rider.id}</span>
                          </td>
                          <td className="px-6 py-4 font-medium text-slate-700 dark:text-slate-300">
                            {prov ? prov.full_name : <span className="text-slate-400 italic">No provider registered</span>}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col gap-1.5 items-start">
                              <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                                rider.status === 'suspended'
                                  ? 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400'
                                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400'
                              }`}>
                                {rider.status}
                              </span>
                              {prov && (
                                <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
                                  prov.is_online
                                    ? 'bg-green-500/10 text-green-600 border border-green-500/20'
                                    : 'bg-slate-100 text-slate-500 dark:bg-slate-800/50'
                                }`}>
                                  {prov.is_online ? 'ONLINE' : 'OFFLINE'}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 font-mono text-slate-600 dark:text-slate-400">
                            {prov?.vehicle_number || 'N/A'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1">
                              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                              <span className="font-bold">{prov?.rating?.toFixed(1) || 'N/A'}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            {rider.status !== 'suspended' ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="destructive"
                                disabled={blockingId === rider.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmAction({ tenantId: rider.id, tenantName: rider.business_name || 'Individual Rider', nextStatus: 'suspended' });
                                }}
                                className="h-7 px-2.5 text-[10px] font-bold cursor-pointer pointer-events-auto relative z-10"
                              >
                                <Ban className="w-3 h-3 mr-1" /> Block Rider
                              </Button>
                            ) : (
                              <div className="flex items-center justify-end gap-2">
                                <span className="text-[10px] text-red-500 font-bold italic">Blocked</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={blockingId === rider.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmAction({ tenantId: rider.id, tenantName: rider.business_name || 'Individual Rider', nextStatus: 'active' });
                                  }}
                                  className="h-7 px-2.5 text-[10px] font-bold text-emerald-600 border-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 cursor-pointer pointer-events-auto relative z-10"
                                >
                                  Reactivate
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
            )}
          </div>

          {/* SECTION B: FLEET OWNERS */}
          <div className="space-y-3">
            <h3 className="text-md font-bold text-slate-900 dark:text-slate-150 flex items-center gap-2">
              <Truck className="w-5 h-5 text-indigo-600" />
              <span>Fleet Owners ({fleetBusinesses.length})</span>
            </h3>
            {fleetBusinesses.length === 0 ? (
              <p className="text-xs text-slate-400 dark:text-slate-500 italic p-4 border border-dashed rounded-lg">
                No active fleet owner accounts found.
              </p>
            ) : (
              <div className="space-y-4">
                {fleetBusinesses.map((fleet) => {
                  const fleetDrivers = providers.filter(p => p.tenant_id === fleet.id);
                  const isExpanded = !!expandedFleets[fleet.id];
                  return (
                    <Card key={fleet.id} className="dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                      {/* Accordion Header */}
                      <div className="p-4 flex items-center justify-between gap-4 bg-slate-50/50 dark:bg-slate-950/20 transition-colors select-none">
                        <div className="flex items-center gap-3">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 cursor-pointer"
                            onClick={() => toggleExpandFleet(fleet.id)}
                          >
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </Button>
                          <div>
                            <span className="font-bold text-slate-900 dark:text-white block">{fleet.business_name}</span>
                            <span className="text-[10px] text-slate-400">Tenant ID: {fleet.id} · Drivers Registered: {fleetDrivers.length}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                            fleet.status === 'suspended'
                              ? 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400'
                              : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400'
                          }`}>
                            {fleet.status}
                          </span>
                          {fleet.status !== 'suspended' ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              disabled={blockingId === fleet.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmAction({ tenantId: fleet.id, tenantName: fleet.business_name, nextStatus: 'suspended' });
                              }}
                              className="h-7 px-2.5 text-[10px] font-bold cursor-pointer pointer-events-auto relative z-10"
                            >
                              <Ban className="w-3 h-3 mr-1" /> Suspend Fleet
                            </Button>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-red-500 font-bold italic mr-2">Blocked</span>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={blockingId === fleet.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmAction({ tenantId: fleet.id, tenantName: fleet.business_name, nextStatus: 'active' });
                                }}
                                className="h-7 px-2.5 text-[10px] font-bold text-emerald-600 border-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 cursor-pointer pointer-events-auto relative z-10"
                              >
                                Reactivate
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Accordion Content (Driver List) */}
                      {isExpanded && (
                        <div className="border-t border-slate-150 dark:border-slate-800 p-4 bg-white dark:bg-slate-900/50">
                          <div className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-wide">Fleet Drivers List</div>
                          {fleetDrivers.length === 0 ? (
                            <p className="text-xs text-slate-400 italic py-2">No drivers found registered for this fleet owner.</p>
                          ) : (
                            <div className="overflow-x-auto">
                              <table className="w-full text-left text-xs">
                                <thead className="bg-slate-50/50 dark:bg-slate-950/10 text-slate-500 font-bold border-b border-slate-100 dark:border-slate-800">
                                  <tr>
                                    <th className="px-4 py-2">Full Name</th>
                                    <th className="px-4 py-2">Online Status</th>
                                    <th className="px-4 py-2">Vehicle Number</th>
                                    <th className="px-4 py-2">Rating</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/30">
                                  {fleetDrivers.map((driver) => (
                                    <tr key={driver.id} className="hover:bg-slate-50/20 text-slate-700 dark:text-slate-300">
                                      <td className="px-4 py-2.5 font-bold">{driver.full_name}</td>
                                      <td className="px-4 py-2.5">
                                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold ${
                                          driver.is_online
                                            ? 'bg-green-500/10 text-green-600 border border-green-500/20'
                                            : 'bg-slate-100 text-slate-500 dark:bg-slate-850 dark:text-slate-400'
                                        }`}>
                                          {driver.is_online ? 'ONLINE' : 'OFFLINE'}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2.5 font-mono">{driver.vehicle_number || 'N/A'}</td>
                                      <td className="px-4 py-2.5">
                                        <div className="flex items-center gap-1">
                                          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                                          <span>{driver.rating?.toFixed(1) || 'N/A'}</span>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* SECTION C: PUBLIC TRANSPORT */}
          {publicTransports.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-md font-bold text-slate-900 dark:text-slate-150 flex items-center gap-2">
                <Truck className="w-5 h-5 text-indigo-600" />
                <span>Public Transport Systems ({publicTransports.length})</span>
              </h3>
              <Card className="dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-400 font-bold border-b border-slate-200 dark:border-slate-800">
                    <tr>
                      <th className="px-6 py-3">Operator Name</th>
                      <th className="px-6 py-3">Tenant ID</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-150 dark:divide-slate-800/60">
                    {publicTransports.map((op) => (
                      <tr key={op.id} className="hover:bg-slate-50/40 dark:hover:bg-slate-950/20 text-xs">
                        <td className="px-6 py-4 font-bold text-slate-900 dark:text-white">
                          {op.business_name}
                        </td>
                        <td className="px-6 py-4 font-mono text-slate-500 dark:text-slate-400">
                          {op.id}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                            op.status === 'suspended'
                              ? 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400'
                              : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400'
                          }`}>
                            {op.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          {op.status !== 'suspended' ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              disabled={blockingId === op.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmAction({ tenantId: op.id, tenantName: op.business_name, nextStatus: 'suspended' });
                              }}
                              className="h-7 px-2.5 text-[10px] font-bold cursor-pointer pointer-events-auto relative z-10"
                            >
                              <Ban className="w-3 h-3 mr-1" /> Suspend System
                            </Button>
                          ) : (
                            <div className="flex items-center justify-end gap-2">
                              <span className="text-[10px] text-red-500 font-bold italic">Suspended</span>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                disabled={blockingId === op.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmAction({ tenantId: op.id, tenantName: op.business_name, nextStatus: 'active' });
                                }}
                                className="h-7 px-2.5 text-[10px] font-bold text-emerald-600 border-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 cursor-pointer pointer-events-auto relative z-10"
                              >
                                Reactivate
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          )}

        </div>
      )}

      {confirmAction && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-xl shadow-xl max-w-sm w-full">
            <h4 className="text-md font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${confirmAction.nextStatus === 'active' ? 'text-emerald-500' : 'text-red-500'} animate-pulse`} />
              <span>{confirmAction.nextStatus === 'active' ? 'Confirm Reactivate / Activate' : 'Confirm Suspend / Block'}</span>
            </h4>
            <p className="text-xs text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
              {confirmAction.nextStatus === 'active' ? (
                <>Kya aap sach mein <span className="font-bold text-slate-900 dark:text-white">"{confirmAction.tenantName}"</span> ko wapas Active karna chahte hain?</>
              ) : (
                <>Kya aap sach mein <span className="font-bold text-slate-900 dark:text-white">"{confirmAction.tenantName}"</span> ko Suspend/Block karna chahte hain? Isse iske saare drivers/staff automatically offline ho jaayenge.</>
              )}
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs cursor-pointer"
                onClick={() => setConfirmAction(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant={confirmAction.nextStatus === 'active' ? 'default' : 'destructive'}
                size="sm"
                className={`h-8 text-xs cursor-pointer ${confirmAction.nextStatus === 'active' ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-none' : ''}`}
                onClick={async () => {
                  const targetId = confirmAction.tenantId;
                  const targetStatus = confirmAction.nextStatus;
                  setConfirmAction(null);
                  await executeSetTenantStatus(targetId, targetStatus);
                }}
              >
                {confirmAction.nextStatus === 'active' ? 'Confirm Activate' : 'Confirm Suspend'}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
